// src/personel-takip/src/pages/KayitlarPage.js
import React, { useEffect, useMemo, useState } from "react";
import { db, storage, auth } from "../firebaseConfig";
import {
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  doc as fsDoc,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import Select from "react-select";

/* ========= Helpers ========= */
const U = (s = "") => (s || "").trim().toUpperCase();
const toNameCase = (s = "") =>
  (s || "")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0]?.toUpperCase() + w.slice(1))
    .join(" ");
const byLocale = (a, b) =>
  (a || "").localeCompare(b || "", "tr", { sensitivity: "base" });

const aktifMi = (tarih = {}) => {
  const g = Object.keys(tarih || {}).filter((k) => k.startsWith("giris")).length;
  const c = Object.keys(tarih || {}).filter((k) => k.startsWith("cikis")).length;
  return g > c;
};

const sanitizeFileName = (name = "") => name.replace(/[^a-zA-Z0-9._-]/g, "_");

const toDDMMYYYYDash = (val = "") => {
  const s = (val || "").trim();
  if (!s) return "";
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  return s.replaceAll(".", "-").replaceAll("/", "-");
};

const fromDDMMYYYY_to_YYYYMMDD = (ddmmyyyy = "") => {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(ddmmyyyy);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const fmtDt = (d) => {
  try {
    const date =
      d?.toDate?.() ??
      (Number.isFinite(d?._seconds)
        ? new Date(d._seconds * 1000)
        : Number.isFinite(d?.seconds)
        ? new Date(d.seconds * 1000)
        : new Date(d));
    if (!(date instanceof Date) || isNaN(date)) return "";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, "0");
    const mi = String(date.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yyyy} ${hh}:${mi}`;
  } catch {
    return "";
  }
};

const nowMs = () => Date.now();
const nowISO = () => new Date().toISOString();

const TIP_LABEL = {
  PRIM: "Prim",
  TESKKUR: "Teşekkür",
  UYARI: "Uyarı",
  TUTANAK: "Tutanak",
};

const TIP_OPTIONS = [
  { value: "PRIM", label: "Prim" },
  { value: "TESKKUR", label: "Teşekkür" },
  { value: "UYARI", label: "Uyarı" },
  { value: "TUTANAK", label: "Tutanak" },
];

/* ---- react-select ortak stil ---- */
const selectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: 8,
    minHeight: 38,
    borderColor: "#d1d5db",
    boxShadow: "none",
    ":hover": { borderColor: "#9ca3af" },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#eef2ff",
    borderRadius: 999,
  }),
  multiValueLabel: (base) => ({ ...base, fontWeight: 600 }),
  menu: (base) => ({ ...base, zIndex: 5 }),
};

/* ============================ PAGE ============================ */
const KayitlarPage = () => {
  /* ------------ Kaynak Veriler ------------ */
  const [personeller, setPersoneller] = useState([]);
  const [allowedTesis, setAllowedTesis] = useState([]);
  const [allRecords, setAllRecords] = useState([]); // özet liste ve filtreler için

  /* ------------ Form State (listeden bağımsız) ------------ */
  const [formPersonId, setFormPersonId] = useState(""); // formdaki kişi seçimi
  const [tip, setTip] = useState(""); // PRIM | TESKKUR | UYARI | TUTANAK
  const [aciklama, setAciklama] = useState("");
  const [tutar, setTutar] = useState(""); // PRIM için
  const [file, setFile] = useState(null); // TUTANAK için
  const [recDate, setRecDate] = useState(""); // YYYY-MM-DD
  const [saving, setSaving] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  /* ------------ Kişi Bazlı History ------------ */
  const [historyRows, setHistoryRows] = useState([]);

  /* ------------ Liste/Filtre alanı ------------ */
  const [showPassive, setShowPassive] = useState(false);
  const [textSearch, setTextSearch] = useState("");
  const [selTypes, setSelTypes] = useState([]); // varsayılan boş = hepsi görünür
  const [listSelectedId, setListSelectedId] = useState(""); // sadece listede highlight için

  /* ---------- Görülebilir tesisler ---------- */
  useEffect(() => {
    (async () => {
      try {
        const emailLc = auth?.currentUser?.email?.toLowerCase();
        if (!emailLc) {
          setAllowedTesis([]);
          return;
        }
        const qMe = query(
          collection(db, "personeller"),
          where("email", "==", emailLc)
        );
        const sMe = await getDocs(qMe);
        const own = new Set();
        if (!sMe.empty) {
          sMe.forEach((d) => {
            const data = d.data() || {};
            if (data.tesis) own.add(U(data.tesis));
            let extra = data.gorebilecegiTesisler;
            if (typeof extra === "string") {
              extra = extra.split(",").map((x) => x.trim()).filter(Boolean);
            }
            (extra || []).forEach((t) => own.add(U(t)));
          });
        }
        setAllowedTesis(Array.from(own));
      } catch (e) {
        console.error("[Kayitlar] allowedTesis error:", e);
        setAllowedTesis([]);
      }
    })();
  }, [auth?.currentUser?.email]);

  /* ---------- Personeller ---------- */
  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, "personeller"));
      const arr = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      arr.sort(
        (a, b) =>
          byLocale(a.ad, b.ad) ||
          byLocale(a.soyad, b.soyad) ||
          byLocale(a.tesis, b.tesis)
      );
      setPersoneller(arr);
    })();
  }, []);

  const visiblePersons = useMemo(() => {
    const allow = new Set(allowedTesis);
    return personeller
      .filter((p) => allow.has(U(p.tesis || "")))
      .filter((p) => (showPassive ? true : aktifMi(p.tarih || {})));
  }, [personeller, allowedTesis, showPassive]);

  const personSelectOptions = useMemo(() => {
    return visiblePersons
      .map((p) => ({
        value: p.id,
        label:
          `${toNameCase(p.ad || "")} ${U(p.soyad || "")}` +
          (p.tesis ? ` — ${U(p.tesis)}` : ""),
      }))
      .sort((a, b) => byLocale(a.label, b.label));
  }, [visiblePersons]);

  const selectedPerson = useMemo(
    () => personeller.find((p) => p.id === formPersonId),
    [personeller, formPersonId]
  );

  /* ---------- Tüm kayıtlar (özet liste) ---------- */
  useEffect(() => {
    (async () => {
      try {
        const s = await getDocs(collection(db, "personelKayitlar"));
        const rows = s.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setAllRecords(rows);
      } catch (e) {
        console.warn("[Kayitlar] personelKayitlar okunamadı:", e?.message);
        setAllRecords([]);
      }
    })();
  }, []);

  /* ---------- Seçili kişinin geçmişi ---------- */
  useEffect(() => {
    (async () => {
      setHistoryRows([]);
      if (!selectedPerson) return;
      try {
        const qHist = query(
          collection(db, "personelKayitlar"),
          where("personelId", "==", selectedPerson.id),
          orderBy("createdAt", "desc")
        );
        const s = await getDocs(qHist);
        setHistoryRows(s.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
      } catch {
        const s = await getDocs(
          query(
            collection(db, "personelKayitlar"),
            where("personelId", "==", selectedPerson.id)
          )
        );
        const rows = s.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        rows.sort(
          (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
        );
        setHistoryRows(rows);
      }
    })();
  }, [selectedPerson?.id]);

  /* ---------- Validasyon (KAYDET için tip hâlâ zorunlu) ---------- */
  const validate = () => {
    if (!selectedPerson) return "Lütfen personel seçin.";
    if (!tip) return "Lütfen kayıt tipini seçin."; // sadece KAYDET sırasında kontrol
    if (!/^\d{4}-\d{2}-\d{2}$/.test(recDate))
      return "Lütfen geçerli bir tarih seçin.";

    if (tip === "PRIM") {
      if (!tutar || isNaN(Number(tutar)) || Number(tutar) <= 0) {
        return "Prim için geçerli bir tutar girin.";
      }
    }
    if (tip === "TESKKUR" || tip === "UYARI") {
      if (!aciklama.trim()) return "Açıklama zorunludur.";
    }
    if (tip === "TUTANAK") {
      if (!file) return "Tutanak için PDF yüklemek zorunludur.";
      const name = file.name || "";
      const isPdf =
        (file.type && /pdf/i.test(file.type)) || /\.pdf$/i.test(name);
      if (!isPdf) return "Tutanak dosyası PDF olmalıdır.";
    }
    return "";
  };

  /* ---------- Upload (tutanak) ---------- */
  const uploadTutanakAndGetUrl = async (p, pickedFile) => {
    const safe = sanitizeFileName(
      `${toNameCase(p.ad || "")} ${U(p.soyad || "")} - TUTANAK.pdf`
    );
    const path = `personel-belgeleri/tutanaklar/${p.id}/${Date.now()}-${safe}`;
    const sref = ref(storage, path);
    const task = uploadBytesResumable(sref, pickedFile);

    await new Promise((resolve, reject) => {
      task.on(
        "state_changed",
        (snap) => {
          const pct = Math.round(
            (snap.bytesTransferred / snap.totalBytes) * 100
          );
          setUploadPct(pct);
        },
        (err) => reject(err),
        () => resolve()
      );
    });

    const fileUrl = await getDownloadURL(sref);
    return { fileUrl, storagePath: path };
  };

  /* ---------- Kayıt Ekle ---------- */
  const handleSave = async () => {
    const err = validate();
    if (err) return alert(err);

    try {
      setSaving(true);
      setUploadPct(0);

      const p = selectedPerson;
      const createdBy = auth?.currentUser?.email || "-";
      const tarihDDMMYYYY = toDDMMYYYYDash(recDate);

      let fileUrl = "";
      let storagePath = "";
      let payloadExtra = {};

      if (tip === "TUTANAK") {
        try {
          const up = await uploadTutanakAndGetUrl(p, file);
          fileUrl = up.fileUrl;
          storagePath = up.storagePath;
        } catch (e) {
          console.error("[Kayitlar] PDF upload hatası:", e);
          alert("PDF yüklenemedi. Lütfen tekrar deneyin.");
          setSaving(false);
          return;
        }
      }
      if (tip === "PRIM") payloadExtra.primTutar = Number(tutar);
      if (tip === "TESKKUR" || tip === "UYARI")
        payloadExtra.aciklama = aciklama.trim();

      const basePayload = {
        personelId: p.id,
        personelAdSoyad: `${toNameCase(p.ad || "")} ${U(p.soyad || "")}`,
        gorev: U(p.gorev || ""),
        tesis: U(p.tesis || ""),
        tip,
        tarih: tarihDDMMYYYY,
        fileUrl,
        storagePath,
        createdBy,
        createdAt: serverTimestamp(),
        isDeleted: false,
      };
      const refDoc = await addDoc(
        collection(db, "personelKayitlar"),
        { ...basePayload, ...payloadExtra }
      );

      try {
        await updateDoc(fsDoc(db, "personelKayitlar", refDoc.id), {
          auditTrail: arrayUnion({
            action: "create",
            atMs: nowMs(),
            atISO: nowISO(),
            by: createdBy,
            tip,
          }),
        });
      } catch (e) {
        console.warn("[Kayitlar] auditTrail yazılamadı (devam):", e?.message);
      }

      setTip("");
      setAciklama("");
      setTutar("");
      setFile(null);
      setUploadPct(0);
      setRecDate("");

      const instant = {
        id: refDoc.id,
        ...basePayload,
        ...payloadExtra,
        createdAt: { toDate: () => new Date() },
        auditTrail: [
          { action: "create", atISO: nowISO(), atMs: nowMs(), by: createdBy, tip },
        ],
      };
      setHistoryRows((prev) => [instant, ...prev]);
      setAllRecords((prev) => [instant, ...prev]);

      alert("✅ Kayıt eklendi.");
    } catch (e) {
      console.error("[Kayitlar] Kayıt ekleme hatası:", e);
      alert("❌ Kayıt eklenemedi. Konsolu kontrol edin (hata loglandı).");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Özet liste (filtrelenmiş) ---------- */
  const visibleIds = useMemo(
    () => new Set(visiblePersons.map((p) => p.id)),
    [visiblePersons]
  );

  const personSummaryMap = useMemo(() => {
    const map = new Map();
    for (const p of visiblePersons) {
      map.set(p.id, {
        id: p.id,
        name: `${toNameCase(p.ad || "")} ${U(p.soyad || "")}`,
        gorev: U(p.gorev || ""),
        tesis: U(p.tesis || ""),
        prim: 0,
        teskkur: 0,
        uyari: 0,
        tutanak: 0,
        lastAt: 0,
      });
    }
    for (const r of allRecords) {
      if (!visibleIds.has(r.personelId)) continue;
      if (r.isDeleted) continue; // <<< SOFT-DELETED KAYITLARI SAYMA
      const slot = map.get(r.personelId);
      if (!slot) continue;
      if (r.tip === "PRIM") slot.prim += 1;
      else if (r.tip === "TESKKUR") slot.teskkur += 1;
      else if (r.tip === "UYARI") slot.uyari += 1;
      else if (r.tip === "TUTANAK") slot.tutanak += 1;
      const ts = r.createdAt?._seconds || r.createdAt?.seconds || 0;
      if (ts > slot.lastAt) slot.lastAt = ts;
    }
    return map;
  }, [visiblePersons, allRecords, visibleIds]);

  const filteredSummaryRows = useMemo(() => {
    const typeSet = new Set(selTypes);
    const rows = Array.from(personSummaryMap.values());
    return rows
      .filter((x) => {
        if (textSearch) {
          const s = textSearch.toLowerCase();
          const hay = `${x.name} ${x.gorev} ${x.tesis}`.toLowerCase();
          if (!hay.includes(s)) return false;
        }
        if (typeSet.size === 0) return true;
        const has =
          (typeSet.has("PRIM") && x.prim > 0) ||
          (typeSet.has("TESKKUR") && x.teskkur > 0) ||
          (typeSet.has("UYARI") && x.uyari > 0) ||
          (typeSet.has("TUTANAK") && x.tutanak > 0);
        return has;
      })
      .sort((a, b) => byLocale(a.name, b.name));
  }, [personSummaryMap, selTypes, textSearch]);

  /* ---------- Düzenle / Sil ---------- */
  const [editOpen, setEditOpen] = useState(false);
  const [editRec, setEditRec] = useState(null);
  const [editDate, setEditDate] = useState(""); // YYYY-MM-DD
  const [editAciklama, setEditAciklama] = useState("");
  const [editTutar, setEditTutar] = useState("");
  const [editFile, setEditFile] = useState(null);
  const [editUploadPct, setEditUploadPct] = useState(0);
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (rec) => {
    setEditRec(rec);
    const d = rec?.tarih ? fromDDMMYYYY_to_YYYYMMDD(rec.tarih) : "";
    setEditDate(d || "");
    setEditAciklama(rec?.aciklama || "");
    setEditTutar(rec?.primTutar != null ? String(rec.primTutar) : "");
    setEditFile(null);
    setEditUploadPct(0);
    setEditSaving(false);
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditRec(null);
  };

  const saveEdit = async () => {
    if (!editRec) return;
    const updates = {};
    const editor = auth?.currentUser?.email || "-";

    if (!/^\d{4}-\d{2}-\d{2}$/.test(editDate))
      return alert("Lütfen geçerli düzenleme tarihi seçin.");
    updates.tarih = toDDMMYYYYDash(editDate);

    if (editRec.tip === "PRIM") {
      if (!editTutar || isNaN(Number(editTutar)) || Number(editTutar) <= 0) {
        return alert("Prim için geçerli bir tutar girin.");
      }
      updates.primTutar = Number(editTutar);
    }

    if (editRec.tip === "TESKKUR" || editRec.tip === "UYARI") {
      if (!editAciklama.trim()) return alert("Açıklama zorunludur.");
      updates.aciklama = editAciklama.trim();
    }

    if (editRec.tip === "TUTANAK" && editFile) {
      try {
        setEditSaving(true);
        const p = personeller.find((p) => p.id === editRec.personelId);
        const safe = sanitizeFileName(
          `${toNameCase(p?.ad || "")} ${U(p?.soyad || "")} - TUTANAK.pdf`
        );
        const path = `personel-belgeleri/tutanaklar/${editRec.personelId}/${Date.now()}-${safe}`;
        const sref = ref(storage, path);
        const task = uploadBytesResumable(sref, editFile);
        await new Promise((resolve, reject) => {
          task.on(
            "state_changed",
            (snap) => {
              const pct = Math.round(
                (snap.bytesTransferred / snap.totalBytes) * 100
              );
              setEditUploadPct(pct);
            },
            (err) => reject(err),
            () => resolve()
          );
        });
        const fileUrl = await getDownloadURL(sref);
        updates.fileUrl = fileUrl;
        updates.storagePath = path;
      } catch (e) {
        console.error("[Kayitlar] düzenle-upload hatası:", e);
        alert("Yeni PDF yüklenemedi.");
        setEditSaving(false);
        return;
      }
    }

    updates.editedAt = serverTimestamp();
    updates.editedBy = editor;

    try {
      setEditSaving(true);
      const refDoc = fsDoc(db, "personelKayitlar", editRec.id);
      await updateDoc(refDoc, {
        ...updates,
        auditTrail: arrayUnion({
          action: "edit",
          atMs: nowMs(),
          atISO: nowISO(),
          by: editor,
          changed: Object.keys(updates),
        }),
      });

      const mergeRec = {
        ...editRec,
        ...updates,
        auditTrail: [
          ...(editRec.auditTrail || []),
          {
            action: "edit",
            atMs: nowMs(),
            atISO: nowISO(),
            by: editor,
            changed: Object.keys(updates),
          },
        ],
      };
      setHistoryRows((prev) => prev.map((r) => (r.id === editRec.id ? mergeRec : r)));
      setAllRecords((prev) => prev.map((r) => (r.id === editRec.id ? mergeRec : r)));

      alert("✅ Kayıt güncellendi.");
      closeEdit();
    } catch (e) {
      console.error("[Kayitlar] düzenleme hatası:", e);
      alert("Güncelleme başarısız.");
      setEditSaving(false);
    }
  };

  const softDelete = async (rec) => {
    const ok = window.confirm("Bu kaydı silmek (soft-sil) istiyor musunuz?");
    if (!ok) return;
    try {
      const refDoc = fsDoc(db, "personelKayitlar", rec.id);
      const by = auth?.currentUser?.email || "-";
      await updateDoc(refDoc, {
        isDeleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: by,
        auditTrail: arrayUnion({
          action: "soft-delete",
          atMs: nowMs(),
          atISO: nowISO(),
          by,
        }),
      });

      const mergeRec = {
        ...rec,
        isDeleted: true,
        deletedAt: new Date(),
        deletedBy: by,
        auditTrail: [
          ...(rec.auditTrail || []),
          { action: "soft-delete", atISO: nowISO(), atMs: nowMs(), by },
        ],
      };
      setHistoryRows((prev) => prev.map((r) => (r.id === rec.id ? mergeRec : r)));
      setAllRecords((prev) => prev.map((r) => (r.id === rec.id ? mergeRec : r)));

      alert("🗑️ Kayıt soft-silindi (history’de görünür).");
    } catch (e) {
      console.error("[Kayitlar] soft-delete hatası:", e);
      alert("Silme başarısız.");
    }
  };

  /* ---------- Değişiklik açıklaması üretici ---------- */
  const changeLabel = (fields = []) => {
    const map = {
      tarih: "tarih değiştirildi",
      primTutar: "prim tutarı değiştirildi",
      aciklama: "açıklama değiştirildi",
      fileUrl: "tutanak dosyası değiştirildi",
      storagePath: "tutanak dosyası değiştirildi",
      editedAt: "düzenlendi",
    };
    const uniq = Array.from(new Set(fields || []));
    if (!uniq.length) return "düzenlendi";
    const labels = uniq.map((k) => map[k] || k + " değiştirildi");
    return labels.join(", ");
  };

  const toDDMMYYYY_HHMM = (iso) => {
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}-${mm}-${yy} ${hh}:${mi}`;
  };

  const renderSignCell = (r) => {
    const created = `Ekleyen: ${r.createdBy || "-"}${
      r.createdAt ? ` (${fmtDt(r.createdAt)})` : ""
    }`;
    const edits = (r.auditTrail || [])
      .filter((a) => a.action === "edit")
      .map(
        (a) =>
          `Düzenleyen: ${a.by || "-"}${
            a.atISO ? ` (${toDDMMYYYY_HHMM(a.atISO)})` : ""
          } — ${changeLabel(a.changed)}`
      );
    const del = (r.auditTrail || []).find((a) => a.action === "soft-delete");
    const delStr = del
      ? `Silme: ${del.by || "-"}${
          del.atISO ? ` (${toDDMMYYYY_HHMM(del.atISO)})` : ""
        } — silindi`
      : "";

    const lines = [created, ...edits, delStr].filter(Boolean);
    return (
      <div style={{ fontSize: 11, lineHeight: 1.35 }}>
        {lines.map((t, i) => (
          <div key={i}>{t}</div>
        ))}
      </div>
    );
  };

  /* ---------- Stil & UI ---------- */
  return (
    <div className="page">
      <style>{`
        .page{ padding:14px; background:#fff; font-family:system-ui,-apple-system,"Segoe UI",Roboto,Arial; }
        .panel{ border:1px solid #e5e7eb; border-radius:10px; background:#fff; }
        .panel .head{ padding:10px 12px; border-bottom:1px solid #e5e7eb; font-weight:700; display:flex; align-items:center; justify-content:space-between; gap:8px; }
        .panel .body{ padding:10px 12px; }
        .row{ display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
        .muted{ color:#6b7280; font-size:12px; }
        input[type="text"], input[type="date"], input[type="number"], textarea, select {
          padding:8px 10px; border:1px solid #d1d5db; border-radius:8px; font-size:13px; box-sizing:border-box;
        }
        textarea{ width:100%; min-height:90px; resize:vertical; }
        .btn{ padding:8px 12px; border-radius:8px; border:1px solid #1f2937; background:#111827; color:#fff; font-weight:600; cursor:pointer; }
        .btn:disabled{ opacity:.6; cursor:not-allowed; }
        .ghost{ background:#fff; color:#111827; border-color:#e5e7eb; }
        .list{ max-height: calc(100vh - 320px); overflow:auto; }
        .person{ display:flex; align-items:center; justify-content:space-between; gap:8px; padding:8px; border:1px solid #e5e7eb; border-radius:8px; margin-bottom:6px; cursor:pointer; font-size:12px; }
        .person:hover{ background:#f8fafc; }
        .person.active{ background:#eef2ff; border-color:#c7d2fe; }
        table{ width:100%; border-collapse:collapse; font-size:12px; }
        th, td{ border:1px solid #e5e7eb; padding:6px; text-align:left; vertical-align:middle; }
        thead th{ background:#f8fafc; }
        .chip{ padding:2px 8px; border:1px solid #e5e7eb; background:#f1f5f9; border-radius:999px; font-size:11px; }
        .progress{ width:100%; height:8px; border-radius:999px; background:#e5e7eb; overflow:hidden; }
        .progress > div{ height:100%; background:#2563eb; }
        .strike{ text-decoration: line-through; color:#6b7280; }
      `}</style>

      {/* --------- KAYIT EKLE (ÜSTTE) --------- */}
      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="head">Yeni Kayıt Ekle</div>
        <div className="body">
          <div className="row">
            <div style={{ minWidth: 280, flex: "1 1 280px" }}>
              <div className="muted">Ad Soyad</div>
              <Select
                styles={selectStyles}
                options={personSelectOptions}
                value={
                  personSelectOptions.find((o) => o.value === formPersonId) || null
                }
                onChange={(opt) => setFormPersonId(opt?.value || "")}
                placeholder="Personel seç..."
                isClearable
              />
            </div>

            <div style={{ minWidth: 220, flex: "0 0 220px" }}>
              <div className="muted">Kayıt Tipi</div>
              <Select
                styles={selectStyles}
                options={TIP_OPTIONS}
                value={TIP_OPTIONS.find((o) => o.value === tip) || null}
                onChange={(opt) => setTip(opt?.value || "")}
                placeholder="Tip seç (kayıt eklemek için)"
                isClearable
              />
            </div>

            <div style={{ minWidth: 160 }}>
              <div className="muted">Tarih</div>
              <input
                type="date"
                value={recDate}
                onChange={(e) => setRecDate(e.target.value)}
                style={{ width: "100%" }}
              />
            </div>
          </div>

          {tip === "PRIM" && (
            <div className="row" style={{ marginTop: 8 }}>
              <div style={{ minWidth: 180 }}>
                <div className="muted">Tutar</div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={tutar}
                  onChange={(e) => setTutar(e.target.value)}
                  placeholder="Örn: 1000"
                  style={{ width: 180 }}
                />
              </div>
              <div className="muted">Not: Prim için tutar zorunludur.</div>
            </div>
          )}

          {(tip === "TESKKUR" || tip === "UYARI") && (
            <div style={{ marginTop: 8 }}>
              <div className="muted">Açıklama (zorunlu)</div>
              <textarea
                placeholder="Kısa açıklama..."
                value={aciklama}
                onChange={(e) => setAciklama(e.target.value)}
              />
            </div>
          )}

          {tip === "TUTANAK" && (
            <div className="row" style={{ marginTop: 8 }}>
              <div style={{ minWidth: 280, flex: "1 1 280px" }}>
                <div className="muted">Tutanak (PDF - zorunlu)</div>
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          )}

          <div className="row" style={{ alignItems: "center", marginTop: 10 }}>
            <button className="btn" onClick={handleSave} disabled={saving}>
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
            {saving && tip === "TUTANAK" && (
              <div style={{ flex: 1, minWidth: 200 }}>
                <div className="progress" title={`${uploadPct}%`}>
                  <div style={{ width: `${uploadPct}%` }} />
                </div>
                <div className="muted" style={{ marginTop: 4 }}>
                  Yükleme: %{uploadPct}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* --------- KİŞİ ÖZET + GEÇMİŞ --------- */}
      {selectedPerson && (
        <div className="panel" style={{ marginBottom: 12 }}>
          <div className="head">
            <div>
              {toNameCase(selectedPerson.ad || "")} {U(selectedPerson.soyad || "")} —{" "}
              {U(selectedPerson.gorev || "")}
              {selectedPerson.tesis ? ` — ${U(selectedPerson.tesis)}` : ""}
            </div>
            <div className="muted">
              {tip
                ? <>Seçilen Kayıt Tipi: <b>{TIP_LABEL[tip] || tip}</b></>
                : <>Kayıt Tipi seçilmedi — tüm kayıtlar listeleniyor</>}
            </div>
          </div>
          <div className="body">
            <div className="row" style={{ marginBottom: 10 }}>
              {(() => {
                const pid = selectedPerson.id;
                // <<< SADECE SİLİNMEYENLERİ SAY
                const rows = allRecords.filter(
                  (r) => r.personelId === pid && !r.isDeleted
                );
                const prim = rows.filter((r) => r.tip === "PRIM").length;
                const tes = rows.filter((r) => r.tip === "TESKKUR").length;
                const uyr = rows.filter((r) => r.tip === "UYARI").length;
                const tut = rows.filter((r) => r.tip === "TUTANAK").length;
                return (
                  <>
                    <span className="chip">Prim: {prim}</span>
                    <span className="chip">Teşekkür: {tes}</span>
                    <span className="chip">Uyarı: {uyr}</span>
                    <span className="chip">Tutanak: {tut}</span>
                  </>
                );
              })()}
            </div>

            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                Geçmiş Kayıtlar
              </div>
              {historyRows.length ? (
                <table>
                  <thead>
                    <tr>
                      <th>Tarih</th>
                      <th>Tip</th>
                      <th>Detay</th>
                      <th>Dosya</th>
                      <th>İmza (ekleyen / düzenleyen)</th>
                      <th>Durum</th>
                      <th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRows
                      .filter((r) => (tip ? r.tip === tip : true)) /* tip seçiliyse daralt */
                      .map((r) => {
                        const rowCls = r.isDeleted ? "strike" : "";
                        return (
                          <tr key={r.id} className={rowCls}>
                            <td>{r.tarih || "—"}</td>
                            <td>{TIP_LABEL[r.tip] || r.tip || "—"}</td>
                            <td>
                              {r.tip === "PRIM" && r.primTutar != null
                                ? `Tutar: ${r.primTutar}`
                                : r.aciklama || "—"}
                            </td>
                            <td>
                              {r.fileUrl ? (
                                <a
                                  className="btn ghost"
                                  href={r.fileUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Aç
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>{renderSignCell(r)}</td>
                            <td>{r.isDeleted ? "SİLİNDİ" : "Aktif"}</td>
                            <td style={{ display: "flex", gap: 6 }}>
                              <button
                                className="btn ghost"
                                onClick={() => openEdit(r)}
                                disabled={r.isDeleted}
                              >
                                Düzenle
                              </button>
                              <button
                                className="btn"
                                style={{ background: "#991b1b", borderColor: "#991b1b" }}
                                onClick={() => softDelete(r)}
                                disabled={r.isDeleted}
                              >
                                Soft Sil
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              ) : (
                <div className="muted">(Kayıt yok)</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --------- GENEL LİSTE + GELİŞMİŞ TİP FİLTRESİ --------- */}
      <div className="panel">
        <div className="head">Görüntüleyebildiğin Personeller – Özet</div>
        <div className="body">
          <div className="row" style={{ marginBottom: 8 }}>
            <input
              type="text"
              placeholder="Ara (ad / görev / tesis)"
              value={textSearch}
              onChange={(e) => setTextSearch(e.target.value)}
              style={{ flex: "1 1 240px" }}
            />
            <label className="row" style={{ gap: 6, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={showPassive}
                onChange={(e) => setShowPassive(e.target.checked)}
                style={{ cursor: "pointer" }}
              />
              <span className="muted">Çıkanları da göster</span>
            </label>

            <div style={{ minWidth: 320, flex: "0 0 360px" }}>
              <div className="muted">Belge Tipi Filtreleri</div>
              <Select
                styles={selectStyles}
                isMulti
                closeMenuOnSelect={false}
                options={TIP_OPTIONS}
                value={TIP_OPTIONS.filter((o) => selTypes.includes(o.value))}
                onChange={(vals) => setSelTypes((vals || []).map((v) => v.value))}
                placeholder="Tip seç (çoklu)..."
                isClearable
              />
            </div>
          </div>

          <div className="list">
            {filteredSummaryRows.map((x) => (
              <div
                key={x.id}
                className={`person ${x.id === listSelectedId ? "active" : ""}`}
                onClick={() => setListSelectedId(x.id)}
                onDoubleClick={() => {
                  // çift tıklamada formda kişiyi seç; tip seçimi gerekmiyor
                  setFormPersonId(x.id);
                }}
                title="Tek tık: seç / Çift tık: formdaki Ad Soyad alanına yansıt"
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12 }}>{x.name}</div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {x.gorev}
                    {x.tesis ? ` — ${x.tesis}` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span className="chip">Prim: {x.prim}</span>
                  <span className="chip">Teşekkür: {x.teskkur}</span>
                  <span className="chip">Uyarı: {x.uyari}</span>
                  <span className="chip">Tutanak: {x.tutanak}</span>
                </div>
              </div>
            ))}
            {!filteredSummaryRows.length && (
              <div className="muted">(Kayıt yok)</div>
            )}
          </div>
        </div>
      </div>

      {/* --------- DÜZENLE MODAL --------- */}
      {editOpen && editRec && (
        <div
          className="modal-back"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100,
          }}
          onClick={closeEdit}
        >
          <div
            className="modal"
            style={{
              width: "min(680px,94vw)",
              background: "#fff",
              borderRadius: 10,
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="head" style={{ borderBottom: "1px solid #e5e7eb" }}>
              <div style={{ padding: "8px 12px", fontWeight: 700 }}>
                Kayıt Düzenle — {TIP_LABEL[editRec.tip] || editRec.tip}
              </div>
            </div>
            <div className="body" style={{ padding: 12 }}>
              <div className="row" style={{ marginBottom: 8 }}>
                <div style={{ minWidth: 180 }}>
                  <div className="muted">Tarih</div>
                  <input
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    style={{ width: "100%" }}
                  />
                </div>

                {editRec.tip === "PRIM" && (
                  <div style={{ minWidth: 160 }}>
                    <div className="muted">Tutar</div>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editTutar}
                      onChange={(e) => setEditTutar(e.target.value)}
                      style={{ width: "100%" }}
                    />
                  </div>
                )}
              </div>

              {(editRec.tip === "TESKKUR" || editRec.tip === "UYARI") && (
                <div style={{ marginBottom: 8 }}>
                  <div className="muted">Açıklama</div>
                  <textarea
                    value={editAciklama}
                    onChange={(e) => setEditAciklama(e.target.value)}
                  />
                </div>
              )}

              {editRec.tip === "TUTANAK" && (
                <div className="row" style={{ marginBottom: 8 }}>
                  <div style={{ minWidth: 280, flex: "1 1 280px" }}>
                    <div className="muted">Yeni PDF (opsiyonel)</div>
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={(e) => setEditFile(e.target.files?.[0] || null)}
                      style={{ width: "100%" }}
                    />
                  </div>
                  {editSaving && editFile && (
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div className="progress" title={`${editUploadPct}%`}>
                        <div style={{ width: `${editUploadPct}%` }} />
                      </div>
                      <div className="muted" style={{ marginTop: 4 }}>
                        Yükleme: %{editUploadPct}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="row" style={{ justifyContent: "flex-end" }}>
                <button className="btn ghost" onClick={closeEdit} disabled={editSaving}>
                  Vazgeç
                </button>
                <button className="btn" onClick={saveEdit} disabled={editSaving}>
                  {editSaving ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KayitlarPage;
